import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import React, { useState, useCallback, useEffect } from "react";
import { Modal } from "@mui/material";
import PageLoader from "../../../../common/ui/PageLoader";
import { updateUserLabPanel } from "../../../../common/network/Api";
import SegmentedStepper from "../common/SegmentedStepper";
import LabBuilderStep1, { validateStep1 } from "./LabBuilderStep1";
import { useSelector } from "react-redux";
import { RootStateServiceLogicBuilder } from "../../store/service-logic-builder-slice";
import LabBuilderStep2, {
  LabPanelItemInput,
  validateStep2,
} from "./LabBuilderStep2";
import { useAppStore } from "../../../../providers/AppStore";
import { useAuth } from "../../../../providers/AuthProvider";

export interface LabPanelData {
  id: string | null;
  companyid: string | undefined;
  name: string;
  provider: string | null;
  servicetype: number;
  items: LabPanelItemInput[];
}

type Props = {
  openModal: boolean;
  handleClose: () => void;
  onPanelSaved?: () => void | Promise<void>;
  editingPanel?: any;
  onDelete?: () => void;
};

const getEmptyPanelData = (companyId?: string): LabPanelData => ({
  id: null,
  companyid: companyId,
  name: "",
  provider: null,
  servicetype: 6,
  items: [],
});

const mapEditingPanelToData = (
  panel: any,
  companyId?: string
): LabPanelData => ({
  id: panel.id ?? null,
  companyid: panel.companyid ?? companyId,
  name: panel.name ?? "",
  provider: panel.provider ?? null,
  servicetype: panel.servicetype ?? 6,
  items: (panel.items ?? []).map((item: any) => ({
    biomarkerid: item.biomarkerID ?? item.biomarkerId ?? item.biomarkerid ?? "",
    labBiomarkerID: item.labBiomarkerID ?? null,
  })),
});

const NewPanelModal = ({
  openModal,
  handleClose,
  onPanelSaved,
  editingPanel,
  onDelete,
}: Props) => {
  const theme: Theme = useTheme();
  const { ASSetOpenSnackBar, ASSetSnackBarMessage, ASSetTypeSnackBar } =
    useAppStore();
  const { AuthUser } = useAuth();
  // Saved panels, so step 1's validation can refuse a name already in use.
  const savedLabPanels = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.savedLabPanels
  );
  const isEditMode = !!editingPanel;
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [panelData, setPanelData] = useState<LabPanelData>(() =>
    editingPanel
      ? mapEditingPanelToData(editingPanel, AuthUser.pnoe_user_id)
      : getEmptyPanelData(AuthUser.pnoe_user_id)
  );

  useEffect(() => {
    if (editingPanel && openModal) {
      setPanelData(mapEditingPanelToData(editingPanel, AuthUser.pnoe_user_id));
    }
  }, [editingPanel, openModal, AuthUser.pnoe_user_id]);

  const handleCloseFunction = () => {
    handleClose();
    setIsSaving(false);
    setCurrentStep(1);
    setValidationErrors([]);
    setPanelData(
      editingPanel
        ? mapEditingPanelToData(editingPanel, AuthUser.pnoe_user_id)
        : getEmptyPanelData(AuthUser.pnoe_user_id)
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateUserLabPanel(panelData);

      handleCloseFunction();

      if (onPanelSaved) {
        await onPanelSaved();
      }

      ASSetSnackBarMessage({
        title: "Success",
        message: "Panel saved successfully!",
      });
      ASSetTypeSnackBar("success");
      ASSetOpenSnackBar(true);
    } catch (error) {
      console.error("Error saving panel:", error);

      ASSetSnackBarMessage({
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to save panel. Please try again.",
      });
      ASSetTypeSnackBar("alert");
      ASSetOpenSnackBar(true);

      setIsSaving(false);
    }
  };

  const handleStepValidation = (step: number): boolean => {
    setValidationErrors([]);

    if (step === 1) {
      const validation = validateStep1(
        panelData.name,
        panelData.provider,
        savedLabPanels ?? [],
        panelData.id
      );
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return false;
      }
    }

    if (step === 2) {
      const validation = validateStep2(panelData.items);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return false;
      }
    }

    return true;
  };

  const handleNameChange = useCallback((name: string) => {
    setPanelData((prev) => ({ ...prev, name }));
  }, []);

  const handleProviderChange = useCallback((provider: string | null) => {
    setPanelData((prev) => ({ ...prev, provider, items: [] }));
  }, []);

  const handleItemsChange = useCallback((items: LabPanelItemInput[]) => {
    setPanelData((prev) => ({ ...prev, items }));
  }, []);

  return (
    <Modal open={openModal} onClose={handleCloseFunction} disableEnforceFocus>
      <div style={styles.modalContainer(theme)}>
        <div style={styles.modalHeaderContainer}>
          <div style={styles.modalHeaderTitle(theme)}>
            {isEditMode ? "Edit Panel" : "New Panel"}
          </div>
          <div onClick={handleCloseFunction} style={styles.iconWrapper}>
            <XIconSvg />
          </div>
        </div>
        {isSaving ? (
          <PageLoader />
        ) : (
          <SegmentedStepper
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            isNextButtonDisabled={false}
            handleSave={handleSave}
            isSaveDisabled={isSaving}
            startStep={1}
            totalSteps={2}
            onStepValidation={handleStepValidation}
            onDelete={isEditMode ? onDelete : undefined}
            onStepChange={(newStep, direction) => {
              if (direction === "back") {
                setValidationErrors([]);
              }
              setCurrentStep(newStep);
            }}
            validationErrors={validationErrors}
          >
            {currentStep === 1 && (
              <LabBuilderStep1
                name={panelData.name}
                setName={handleNameChange}
                provider={panelData.provider}
                setProvider={handleProviderChange}
              />
            )}
            {currentStep === 2 && (
              <LabBuilderStep2
                provider={panelData.provider}
                selectedItems={panelData.items}
                setSelectedItems={handleItemsChange}
              />
            )}
          </SegmentedStepper>
        )}
      </div>
    </Modal>
  );
};

export default NewPanelModal;

const styles = {
  modalContainer: (theme: any) => ({
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "24px",
    width: "80%",
    minWidth: "400px",
    // Wide enough for step 2's biomarker table (name + category + LOINC +
    // price) to fit on one line per row.
    maxWidth: "1000px",
    height: "90vh",
    borderRadius: "8px",
  }),
  modalHeaderContainer: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
    marginBottom: "24px",
  },
  modalHeaderTitle: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "34px",
    flex: 1,
  }),
  iconWrapper: {
    marginTop: "10px",
    cursor: "pointer",
  },
};
